import { sortByImpactThenStep } from '@/components/analysis/summary/process-summary/tabs/utils/sortReviewItems'
import type { StructuredSummaryData } from '@/components/analysis/summary/types/structuredSummary'
import type { IssueWorkflowStatus, SummaryItem } from '@/components/analysis/summary/types/triage'
import { summaryIdFromBpmnXmlId } from '@/components/bpmn/utils/summaryIdFromBpmnId'

export type ImprovementStatusGetter = (item: SummaryItem) => IssueWorkflowStatus

export function isOpenImprovementStatus(status: IssueWorkflowStatus): boolean {
	return status === 'needs_verification' || status === 'confirmed'
}

export function isDismissedImprovement(
	item: SummaryItem,
	getStatus: ImprovementStatusGetter
): boolean {
	return getStatus(item) === 'dismissed'
}

export function isProcessLevelImprovement(
	item: SummaryItem,
	bpmnElementIds: readonly string[]
): boolean {
	return primaryBpmnIdForItem(item, bpmnElementIds) == null
}

function bpmnIdForSummaryId(
	summaryId: string,
	bpmnElementIds: readonly string[]
): string | undefined {
	return bpmnElementIds.find((bpmnId) => summaryIdFromBpmnXmlId(bpmnId) === summaryId)
}

/**
 * Diagram element ids for this item, in `relatedIds` order.
 * Items with no related id, or whose related ids are not on the diagram, yield [].
 */
export function linkedBpmnIdsForItem(
	item: SummaryItem,
	bpmnElementIds: readonly string[]
): string[] {
	const matches: string[] = []
	const seen = new Set<string>()
	for (const relatedId of item.relatedIds ?? []) {
		if (!relatedId) continue
		const bpmnId = bpmnIdForSummaryId(relatedId, bpmnElementIds)
		if (!bpmnId || seen.has(bpmnId)) continue
		seen.add(bpmnId)
		matches.push(bpmnId)
	}
	return matches
}

/** First related id that exists on the diagram. */
export function primaryBpmnIdForItem(
	item: SummaryItem,
	bpmnElementIds: readonly string[]
): string | undefined {
	return linkedBpmnIdsForItem(item, bpmnElementIds)[0]
}

export interface RelatedStepRef {
	summaryId: string
	bpmnId?: string
	stepNumber?: string
	title?: string
	missing: boolean
}

/**
 * Related steps for the canvas card: every relatedId except the primary, including
 * ids that are not on the current diagram (`missing: true`).
 */
export function relatedStepsForItem(
	item: SummaryItem,
	bpmnElementIds: readonly string[],
	numberMap: Map<string, string>,
	titleMap: Map<string, string> = new Map()
): RelatedStepRef[] {
	const primaryBpmnId = primaryBpmnIdForItem(item, bpmnElementIds)
	const primarySummaryId = primaryBpmnId ? summaryIdFromBpmnXmlId(primaryBpmnId) : undefined
	const seen = new Set<string>()
	const steps: RelatedStepRef[] = []
	for (const relatedId of item.relatedIds ?? []) {
		if (!relatedId || seen.has(relatedId)) continue
		seen.add(relatedId)
		if (relatedId === primarySummaryId) continue
		const bpmnId = bpmnIdForSummaryId(relatedId, bpmnElementIds)
		steps.push({
			summaryId: relatedId,
			bpmnId,
			stepNumber: numberMap.get(relatedId),
			title: titleMap.get(relatedId),
			missing: bpmnId == null,
		})
	}
	return steps
}

export function buildStepTitleMap(
	data: StructuredSummaryData | null | undefined
): Map<string, string> {
	const titles = new Map<string, string>()
	if (!data) return titles
	for (const row of data.activities) titles.set(row.id, row.name)
	for (const row of data.gateways) titles.set(row.id, row.name)
	for (const row of data.merges ?? []) {
		if (row.name) titles.set(row.id, row.name)
	}
	for (const row of data.start_events) titles.set(row.id, row.description)
	for (const row of data.end_events) titles.set(row.id, row.description)
	return titles
}

/**
 * Group non-dismissed improvements onto their primary diagram element only.
 * Other related ids are omitted here and shown only while the review card is open.
 */
export function groupImprovementsByBpmnElement(
	items: SummaryItem[],
	bpmnElementIds: readonly string[],
	getStatus: ImprovementStatusGetter,
	numberMap: Map<string, string>
): Map<string, SummaryItem[]> {
	const grouped = new Map<string, SummaryItem[]>()
	for (const item of items) {
		if (isDismissedImprovement(item, getStatus)) continue
		const bpmnId = primaryBpmnIdForItem(item, bpmnElementIds)
		if (!bpmnId) continue
		const list = grouped.get(bpmnId)
		if (list) {
			list.push(item)
		} else {
			grouped.set(bpmnId, [item])
		}
	}
	for (const [bpmnId, list] of grouped) {
		grouped.set(bpmnId, sortByImpactThenStep(list, numberMap))
	}
	return grouped
}

export function countOnModelImprovements(
	items: SummaryItem[],
	bpmnElementIds: readonly string[],
	getStatus: ImprovementStatusGetter
): number {
	return items.filter(
		(item) =>
			!isDismissedImprovement(item, getStatus) && primaryBpmnIdForItem(item, bpmnElementIds) != null
	).length
}

export function countProcessLevelImprovements(
	items: SummaryItem[],
	bpmnElementIds: readonly string[],
	getStatus: ImprovementStatusGetter
): number {
	return items.filter(
		(item) =>
			!isDismissedImprovement(item, getStatus) && isProcessLevelImprovement(item, bpmnElementIds)
	).length
}
